import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

const NEXT_DAY_DELIVERY_TIME_ZONE = 'Europe/London';
const NEXT_DAY_DELIVERY_CUTOFF_HOUR = 17;

const UK_WEEKDAY_INDEX = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

function getUkDateParts(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: NEXT_DAY_DELIVERY_TIME_ZONE,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);

  return Object.fromEntries(parts.map((part) => [part.type, part.value]));
}

function getUkOffset(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: NEXT_DAY_DELIVERY_TIME_ZONE,
    timeZoneName: 'shortOffset',
  }).formatToParts(date);

  const timeZoneName = parts.find((part) => part.type === 'timeZoneName')?.value || 'GMT';
  const match = timeZoneName.match(/GMT([+-]\d{1,2})(?::(\d{2}))?/);

  if (!match) return 0;

  const hours = Number(match[1]);
  const minutes = Number(match[2] || 0);

  return (hours * 60 + Math.sign(hours) * minutes) * 60000;
}

function getUkCutoffUtc(year, month, day) {
  const utcDate = new Date(Date.UTC(year, month - 1, day, NEXT_DAY_DELIVERY_CUTOFF_HOUR));
  return new Date(utcDate.getTime() - getUkOffset(utcDate));
}

function getUkCutoffFromParts(parts, daysToAdd = 0) {
  const safeUkNoon = new Date(
    Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day) + daysToAdd, 12)
  );
  const targetParts = getUkDateParts(safeUkNoon);

  return getUkCutoffUtc(
    Number(targetParts.year),
    Number(targetParts.month),
    Number(targetParts.day)
  );
}

function getDeliveryState() {
  const now = new Date();
  const ukNow = getUkDateParts(now);
  const weekday = UK_WEEKDAY_INDEX[ukNow.weekday];

  const todayCutoff = getUkCutoffFromParts(ukNow);
  const tomorrowCutoff = getUkCutoffFromParts(ukNow, 1);
  const beforeCutoff = now < todayCutoff;

  if (weekday >= 1 && weekday <= 3) {
    return {
      visible: true,
      deadline: beforeCutoff ? todayCutoff : tomorrowCutoff,
      message: 'left for Next Day Delivery',
    };
  }

  if (weekday === 4) {
    return {
      visible: true,
      deadline: beforeCutoff ? todayCutoff : tomorrowCutoff,
      message: beforeCutoff ? 'left for Next Day Delivery' : 'left for Saturday Delivery',
    };
  }

  if (weekday === 5 && beforeCutoff) {
    return {
      visible: true,
      deadline: todayCutoff,
      message: 'left for Saturday Delivery',
    };
  }

  return {
    visible: false,
    deadline: null,
    message: '',
  };
}

function updateNextDayDeliveryCountdown(root = document) {
  const countdown = root.querySelector('[data-next-day-delivery-countdown]');
  if (!countdown) return;

  const timeElement = countdown.querySelector('[data-next-day-delivery-time]');
  const textElement = countdown.querySelector('[data-next-day-delivery-text]');
  if (!timeElement) return;

  const deliveryState = getDeliveryState();

  if (!deliveryState.visible || !deliveryState.deadline) {
    countdown.hidden = true;
    countdown.setAttribute('aria-hidden', 'true');
    return;
  }

  countdown.hidden = false;
  countdown.removeAttribute('aria-hidden');

  const diff = Math.max(0, deliveryState.deadline.getTime() - Date.now());
  const totalMinutes = Math.floor(diff / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  timeElement.textContent = hours > 0 ? `${hours}hrs ${minutes}mins` : `${minutes}mins`;

  if (textElement) {
    textElement.textContent = deliveryState.message;
  }
}

/**
 * A custom element that manages a cart drawer.
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number | undefined} */
  #nextDayDeliveryTimer;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    window.clearInterval(this.#nextDayDeliveryTimer);
  }

  #handleDialogOpen = () => {
    this.#updateNextDayDeliveryCountdown();
    window.clearInterval(this.#nextDayDeliveryTimer);
    this.#nextDayDeliveryTimer = window.setInterval(() => this.#updateNextDayDeliveryCountdown(), 60000);
  };

  #handleDialogClose = () => {
    window.clearInterval(this.#nextDayDeliveryTimer);
  };

  #updateNextDayDeliveryCountdown() {
    updateNextDayDeliveryCountdown(this.refs.dialog || this);
  }

  #scheduleNextDayDeliveryUpdate() {
    this.#updateNextDayDeliveryCountdown();
    window.setTimeout(() => this.#updateNextDayDeliveryCountdown(), 100);
    window.setTimeout(() => this.#updateNextDayDeliveryCountdown(), 500);
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#scheduleNextDayDeliveryUpdate();
    this.#announceCartCount(event.detail.resource?.item_count);
  };

  #announceCartCount(cartCount) {
    const liveRegion = this.refs.liveRegion;
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();
    this.#scheduleNextDayDeliveryUpdate();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = this.refs;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}